import { useState, useEffect } from 'react';
import { Download, Search, X, CheckCircle, AlertCircle, Loader, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ListingFormation() {
  const [certifications, setCertifications] = useState([]);
  const [selectedCerts, setSelectedCerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [codesInput, setCodesInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchAvailableCertifications();
  }, []);

  const fetchAvailableCertifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/rncp/certifications`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setCertifications(data.data.certifications || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des certifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCertification = (cert) => {
    if (selectedCerts.find(c => c.id === cert.id)) {
      setSelectedCerts(selectedCerts.filter(c => c.id !== cert.id));
    } else {
      setSelectedCerts([...selectedCerts, cert]);
    }
  };

  const handleAddFromCodes = async () => {
    const codes = codesInput.split(';').map(c => c.trim().replace(/^RNCP/i, '')).filter(c => c);

    if (codes.length === 0) {
      toast.error('Aucun code RNCP valide');
      return;
    }

    let addedCount = 0;

    for (const code of codes) {
      // Chercher dans les certifications existantes
      const existingCert = certifications.find(c => c.rncpCode === code);

      if (existingCert) {
        if (!selectedCerts.find(c => c.id === existingCert.id)) {
          setSelectedCerts(prev => [...prev, existingCert]);
          addedCount++;
        }
      } else {
        // Si pas dans la DB, ajouter comme nouveau code à synchroniser
        const newCert = {
          id: `new-${code}`,
          rncpCode: code,
          title: `RNCP ${code} (à synchroniser)`,
          isNew: true
        };

        if (!selectedCerts.find(c => c.rncpCode === code)) {
          setSelectedCerts(prev => [...prev, newCert]);
          addedCount++;
        }
      }
    }

    setCodesInput('');

    if (addedCount > 0) {
      toast.success(`${addedCount} certification(s) ajoutée(s)`);
    } else {
      toast.info('Toutes les certifications sont déjà sélectionnées');
    }
  };

  const handleSync = async () => {
    if (selectedCerts.length === 0) {
      toast.error('Veuillez sélectionner au moins une certification');
      return;
    }

    setSyncing(true);
    try {
      // Utiliser rncpCodes au lieu de certificationIds
      const rncpCodes = selectedCerts.map(c => c.rncpCode);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/rncp/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          rncpCodes: rncpCodes
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message || `Synchronisation terminée : ${selectedCerts.length} certification(s)`);

        // Afficher les détails des résultats
        if (data.data && data.data.results) {
          const created = data.data.results.filter(r => r.action === 'created').length;
          const updated = data.data.results.filter(r => r.action === 'updated').length;
          const failed = data.data.results.filter(r => !r.success).length;

          if (created > 0) toast.success(`${created} certification(s) créée(s)`);
          if (updated > 0) toast.success(`${updated} certification(s) mise(s) à jour`);
          if (failed > 0) toast.error(`${failed} certification(s) en erreur`);
        }

        setSelectedCerts([]);
        fetchAvailableCertifications();
      } else {
        toast.error(data.message || 'Erreur lors de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (certId, certTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${certTitle}" ?\nCette action supprimera également tous les blocs et compétences associés.`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/rncp/certifications/${certId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || 'Certification supprimée avec succès');

        // Afficher les détails de la suppression
        if (data.data) {
          const { deletedBlocks, deletedCompetences } = data.data;
          if (deletedBlocks > 0 || deletedCompetences > 0) {
            toast.success(`Supprimés: ${deletedBlocks} blocs, ${deletedCompetences} compétences`);
          }
        }

        // Retirer de la sélection si elle était sélectionnée
        setSelectedCerts(selectedCerts.filter(c => c.id !== certId));

        // Rafraîchir la liste
        fetchAvailableCertifications();
      } else {
        toast.error(data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredCertifications = certifications.filter(cert =>
    cert.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.rncpCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Download className="w-8 h-8" />
            Synchronisation RNCP
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Sélectionnez les certifications RNCP à synchroniser avec leurs blocs de compétences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des certifications disponibles */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Certifications disponibles</h2>

              {/* Barre de recherche */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou code RNCP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Import par codes RNCP */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajouter par codes RNCP (séparés par ;)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Ex: RNCP34947;RNCP35634;RNCP36520"
                    value={codesInput}
                    onChange={(e) => setCodesInput(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <button
                    onClick={handleAddFromCodes}
                    disabled={!codesInput.trim()}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>

            {/* Liste scrollable des certifications */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : filteredCertifications.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  Aucune certification trouvée
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredCertifications.map((cert) => (
                    <div
                      key={cert.id}
                      className={`flex items-start p-4 hover:bg-gray-50 transition-colors ${
                        selectedCerts.find(c => c.id === cert.id) ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <button
                        onClick={() => handleSelectCertification(cert)}
                        className="flex-1 text-left"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{cert.title}</div>
                            <div className="text-sm text-gray-500 mt-1">
                              {cert.rncpCode} • Niveau {cert.niveau}
                            </div>
                            {cert.secteur && (
                              <div className="text-xs text-gray-400 mt-1">{cert.secteur}</div>
                            )}
                          </div>
                          {selectedCerts.find(c => c.id === cert.id) && (
                            <CheckCircle className="w-5 h-5 text-emerald-500 ml-2" />
                          )}
                        </div>
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(cert.id, cert.title);
                        }}
                        className="ml-2 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer cette certification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Certifications sélectionnées */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Sélectionnées ({selectedCerts.length})
              </h2>
            </div>

            <div className="p-6">
              {selectedCerts.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    Aucune certification sélectionnée
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                    {selectedCerts.map((cert) => (
                      <div
                        key={cert.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {cert.title}
                          </div>
                          <div className="text-xs text-gray-500">{cert.rncpCode}</div>
                        </div>
                        <button
                          onClick={() => setSelectedCerts(selectedCerts.filter(c => c.id !== cert.id))}
                          className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleSync}
                    disabled={syncing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {syncing ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Synchronisation...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Synchroniser {selectedCerts.length} certification(s)
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setSelectedCerts([])}
                    className="w-full mt-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Tout désélectionner
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">À propos de la synchronisation</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>La synchronisation récupère les blocs de compétences et compétences depuis l'API RNCP</li>
                <li>Les données existantes seront mises à jour automatiquement</li>
                <li>Le processus peut prendre quelques minutes selon le nombre de certifications</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
